import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { NoveltyCheckPermissionService, CheckPLgPermissionOptions } from '../novelty-check-permission-service';
import { UserInfoService, UserLevel } from '@telecom/shared/domain/permission/services/user-info-service';
import { CommandService } from '@patsnap/tahiti-core';
import { EditionType } from '@/features/plg-plan/types';
import { getIsOversea } from '@telecom/shared/utils/environment';
import { PlgAgentFeatureCheckApi } from '@telecom/shared/domain/permission/apis/plg-agent-feature-check-api';

/**
 * 查新模块编辑权限服务实现
 */
@injectable()
export class NoveltyCheckPermissionServiceImpl implements NoveltyCheckPermissionService {
    constructor(
        @inject(UserInfoService)
        private readonly _userInfoService: UserInfoService,
        @inject(CommandService)
        private readonly _commandService: CommandService,
        @inject(PlgAgentFeatureCheckApi)
        private readonly _plgAgentFeatureCheckApi: PlgAgentFeatureCheckApi,
    ) {}

    async checkPLgPermission(callback?: () => void | Promise<void>, options?: CheckPLgPermissionOptions): Promise<void> {
        // 中国没有PLG,所以直接放开给客户用
        if (!getIsOversea()) {
            await this._runCallback(callback);
            return;
        }

        const userLevel = await this._userInfoService.userLevel();
        if (userLevel !== UserLevel.Guest && userLevel !== UserLevel.Free) {
            await this._runCallback(callback);
            return;
        }

        if (!options?.taskId) {
            await this._runCallback(callback);
            return;
        }

        const { can_use } = await this._plgAgentFeatureCheckApi.checkPlgAgentFeature({
            task_id: options.taskId,
        });
        if (can_use) {
            await this._runCallback(callback);
            return;
        }

        this._openUpgradeDialog(options);
        throw new Error('No permission to use advanced feature. PLG free quota exceeded.');
    }

    private async _runCallback(callback?: () => void | Promise<void>): Promise<void> {
        if (callback) {
            await callback();
        }
    }

    private _openUpgradeDialog(options?: CheckPLgPermissionOptions): void {
        this._commandService.executeCommand('usage-limit:open-modal', {
            recommendedPlanId: EditionType.ENTERPRISE,
            agent_name: 'novelty',
            trigger_source: options?.trigger_source,
            titleType: 'enterprise_only',
            header_type: 'feature_upgrade_required',
        });
    }
}
